import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  Card,
  CardContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { formatWithOutSom, successMessage } from '../Message';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { approvalReversal, disapprovalReversal, loadReversalRequest } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';

// Move SupervisorDashboard outside as a separate component
const SupervisorDashboard = ({
  filteredRequests,
  searchTerm,
  setSearchTerm,
  dateFilter,
  setDateFilter,
  statusFilter,
  setStatusFilter,
  handleViewDetails,
  handleApprove,
  handleDisapprove,
  getStatusColor
}) => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
    
    {/* Filters and Search */}
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Grid container spacing={2} alignItems="center" columns={{ xs: 4, sm: 8, md: 12 }}>
        <Grid item xs={12} md={6} size={4}>
          <TextField
            fullWidth
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder="Search by receipt N0. Or Patient..... "
          />
        </Grid>
        <Grid item xs={12} md={3} size={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Filter by Date"
              value={dateFilter}
              onChange={setDateFilter}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4} size={2}>
          <FormControl fullWidth>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="disapproved">Disapproved</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2} size={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => {
              setSearchTerm('');
              setDateFilter(null);
              setStatusFilter('all');
            }}
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>

    {/* Requests Table */}
    <Paper elevation={2}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Receipt N0.</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Charge</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Request By</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((request) => (
              <TableRow key={request.id} hover>
                <TableCell>{request.transactionId}</TableCell>
                <TableCell>{request.patient}</TableCell>
                <TableCell>{request.charge}</TableCell>
                <TableCell>
                  {formatWithOutSom(request.amount)}
                </TableCell>
                <TableCell>{request.requestBy}</TableCell>
                <TableCell>
                  {request.time}
                </TableCell>
                <TableCell>
                  <Chip
                    label={request.status.toUpperCase()}
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {request.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(request)}
                            disabled={!dayjs(request.time).isSame(dayjs(), 'day')}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Disapprove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDisapprove(request)}
                            disabled={!dayjs(request.time).isSame(dayjs(), 'day')}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredRequests.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No requests found
          </Typography>
        </Box>
      )}
    </Paper>
  </Container>
);


function ReversalRequest() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);


  const loadreve = async () => {
    try {
      const response = await axios.get(loadReversalRequest);
      if (response.data.success) {
        setRequests(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error'
      });
    }
  }


  const [currentUser ,setcurrentUser]=useState()
  
  useEffect(() => {
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo) {
        const userInfo = JSON.parse(savedUserInfo);
        const userName = userInfo.name || '';
        setcurrentUser(userName)
      } 
    }, []);

  useEffect(() => {
    loadreve();
  }, []);

  useEffect(() => {
    let filtered = requests;
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requestBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.service.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Date filter
    if (dateFilter) {
      filtered = filtered.filter(req =>
        dayjs(req.requestDate).isSame(dayjs(dateFilter), 'day')
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  }, [requests, searchTerm, dateFilter, statusFilter]);

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDialogOpen(true);
  };

  const handleApprove = async(request) => {

    try {
        const result = await Swal.fire({
            title: 'Confirm / Cancel',
            text: "Are You Sure You Want To Approve The Selected Bill Reversal Information",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes Confirm'
        });
    
          if (result.isConfirmed) {
              Swal.fire({
                  title: 'Processing.......',
                  text: 'Please wait',
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                  showConfirmButton: false,
                  didOpen: () => {
                      Swal.showLoading();
                  }
              });
    
                const reversalPayload = {
                   amount : request.amount
                   ,date : request.date
                   ,details : request.details
                   ,id : request.id
                   ,patient : request.patient
                   ,requestBy : request.requestBy
                   ,service : request.service
                   ,serviceStatus : request.serviceStatus
                   ,status : request.status 
                   , time : request.time
                   ,transactionId: request.transactionId ,
                   user: request.user ,
                   currentUser:currentUser ,
                   request_id:request.request_id ,
                   charge:request.charge
                };
    
    
                const response = await axios.post(approvalReversal, reversalPayload);
    
                Swal.close();
    
                if (response.data.success) {
                    successMessage('Save', response.data.message);
                    
                    
                    //setSelectedReason('');
                    //setCustomReason('');
                    //onClose(true);
                } else {
                    throw new Error(response.data.message || 'Reversal failed');
                }
            } else {
               
                return;
            }
    
        } catch (err) {
            console.error('Reversal error:', err);
            
          
            Swal.close();
            
           
            const errorMessage = err.response?.data?.message || 'Failed to process reversal';
            
            //setError(errorMessage);
            
            await Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });
            
        } finally {
           // setLoading(false);
        }
  };

  const handleDisapprove =  async(request) => {

    try {
        const result = await Swal.fire({
            title: 'Confirm / Cancel',
            text: "Are You Sure You Want To Disapprove The Selected Bill Reversal Information",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes Confirm'
        });
    
          if (result.isConfirmed) {
              Swal.fire({
                  title: 'Processing.......',
                  text: 'Please wait',
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                  showConfirmButton: false,
                  didOpen: () => {
                      Swal.showLoading();
                  }
              });
    
                const reversalPayload = {
                   amount : request.amount
                   ,date : request.date
                   ,details : request.details
                   ,id : request.id
                   ,patient : request.patient
                   ,requestBy : request.requestBy
                   ,service : request.service
                   ,serviceStatus : request.serviceStatus
                   ,status : request.status 
                   , time : request.time
                   ,transactionId: request.transactionId ,
                   user: request.user ,
                   currentUser:currentUser ,
                   request_id:request.request_id ,
                   charge:request.charge
                };
    
    
                const response = await axios.post(disapprovalReversal, reversalPayload);
    
                Swal.close();
    
                if (response.data.success) {
                    successMessage('Save', response.data.message);
                    
                    
                    //setSelectedReason('');
                    //setCustomReason('');
                    //onClose(true);
                } else {
                    throw new Error(response.data.message || 'Reversal failed');
                }
            } else {
               
                return;
            }
    
        } catch (err) {
            console.error('Reversal error:', err);
            
          
            Swal.close();
            
           
            const errorMessage = err.response?.data?.message || 'Failed to process reversal';
            
            //setError(errorMessage);
            
            await Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });
            
        } finally {
           // setLoading(false);
        }
  
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'disapproved': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <ReceiptIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Transaction Reversal System
          </Typography>
          <Tooltip title="New Requests">
            <IconButton 
              color="inherit" 
              sx={{ mr: 2 }}
            >
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <SupervisorDashboard 
        requests={requests}
        filteredRequests={filteredRequests}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        handleViewDetails={handleViewDetails}
        handleApprove={handleApprove}
        handleDisapprove={handleDisapprove}
        getStatusColor={getStatusColor}
      />

      {/* Request Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="600" color="primary">
              Request Details
            </Typography>
            <IconButton
              onClick={() => setDialogOpen(false)}
              size="small"
              sx={{ color: 'grey.500' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pb: 1 }}>
          {selectedRequest && (
            <Box>
              {/* Status Alert */}
              <Alert 
                severity={selectedRequest.status === 'approved' ? 'success' : 
                         selectedRequest.status === 'disapproved' ? 'error' : 'warning'}
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  fontWeight: 500,
                  '& .MuiAlert-message': {
                    fontSize: '0.95rem'
                  }
                }}
                icon={
                  selectedRequest.status === 'approved' ? <CheckCircleIcon /> :
                  selectedRequest.status === 'disapproved' ? <ErrorIcon /> :
                  <PendingIcon />
                }
              >
                Status: {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
              </Alert>

              {/* Main Information Card */}
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="600">
                    Transaction Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          RECEIPT N0.
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                          {selectedRequest.transactionId}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          PATIENT ID
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                          {selectedRequest.patient}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          CHARGE
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                          {selectedRequest.charge}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          AMOUNT
                        </Typography>
                        <Typography 
                          variant="h6" 
                          color="primary" 
                          fontWeight="600"
                          sx={{ mt: 0.5 }}
                        >
                          {formatWithOutSom(selectedRequest.amount)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Request Information Card */}
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="600">
                    Request Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          REQUESTED BY
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                          {selectedRequest.requestBy}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          DATE REQUESTED 
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                          {selectedRequest.time}
                        </Typography>
                      </Box>
                    </Grid>

                     <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          APPROVE/DISAPPROVE BY 
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                          {selectedRequest.user}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Details Card */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="600">
                    Additional Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                          REASON FOR REVERSAL
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                          {selectedRequest.details}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {selectedRequest && selectedRequest.status === 'pending' && (
            <>
              <Button
                onClick={() => handleApprove(selectedRequest)}
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                disabled={!dayjs(selectedRequest.time).isSame(dayjs(), 'day')}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Approve Request
              </Button>
              <Button
                onClick={() => handleDisapprove(selectedRequest)}
                variant="contained"
                color="error"
                startIcon={<CloseIcon />}
                disabled={!dayjs(selectedRequest.time).isSame(dayjs(), 'day')}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Disapprove
              </Button>
            </>
          )}
          <Button 
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              ml: 'auto'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default ReversalRequest;